import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, Button, TextField, Chip, IconButton, Grid, Paper, Pagination, PaginationItem } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useDispatch, useSelector } from "react-redux";
import { getAllRegionsAction, clearGetAllRegionsAction } from "../../../store/actions";
import AddRegion from "./AddRegion";

// Listing will come from API via Redux

const Region = () => {
    const [open, setOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [selectedRegionId, setSelectedRegionId] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [page, setPage] = useState(1);
    const rowsPerPage = 10;
    const dispatch = useDispatch();

    // Regions list state
    const { successData: regionsData, loading: regionsLoading } = useSelector((state) => state.getAllRegionsReducer || {});

    // Trust backend contract: regions live at successData.data.regions
    const regions = useMemo(() => {
        return Array.isArray(regionsData?.data?.regions) ? regionsData.data.regions : [];
    }, [regionsData]);

    // Normalize once using stable backend fields
    const normalizedRegions = useMemo(() => {
        return regions.map((r) => ({
            regionId: r.regionId,
            regionName: r.regionName,
            zoneId: r.zoneId,
            zoneName: r.zoneName,
            status: r.status ?? "Active",
        }));
    }, [regions]);

    // Filter/search only over normalized fields
    const filteredRegions = useMemo(() => {
        const q = (searchQuery || "").toString().trim().toLowerCase();
        if (!q) return normalizedRegions;
        return normalizedRegions.filter((r) => {
            const id = String(r.regionId ?? "").toLowerCase();
            const name = (r.regionName ?? "").toLowerCase();
            const zoneId = String(r.zoneId ?? "").toLowerCase();
            const zoneName = (r.zoneName ?? "").toLowerCase();
            const status = (r.status ?? "").toLowerCase();
            return (
                id.includes(q) ||
                name.includes(q) ||
                zoneId.includes(q) ||
                zoneName.includes(q) ||
                status.includes(q)
            );
        });
    }, [normalizedRegions, searchQuery]);

    // Pagination derived values
    const totalPages = useMemo(() => {
        const pages = Math.ceil(filteredRegions.length / rowsPerPage);
        return Math.max(1, pages || 1);
    }, [filteredRegions.length]);

    const paginatedRegions = useMemo(() => {
        const start = (page - 1) * rowsPerPage;
        return filteredRegions.slice(start, start + rowsPerPage);
    }, [filteredRegions, page]);

    const showingStart = filteredRegions.length === 0 ? 0 : ((page - 1) * rowsPerPage + 1);
    const showingEnd = Math.min(page * rowsPerPage, filteredRegions.length);

    useEffect(() => {
        // Fetch regions on mount
        dispatch(clearGetAllRegionsAction());
        dispatch(getAllRegionsAction());
    }, [dispatch]);

    useEffect(() => {
        // Reset page when search filter changes
        setPage(1);
    }, [searchQuery]);

    useEffect(() => {
        // Clamp page if total pages shrink
        if (page > totalPages) {
            setPage(1);
        }
    }, [totalPages, page]);

    const handleAdd = () => {
        setIsEdit(false);
        setSelectedRegionId("");
        setOpen(true);
    };

    const handleEdit = (regionId) => {
        if (!regionId && regionId !== 0) return;
        setIsEdit(true);
        setSelectedRegionId(String(regionId));
        setOpen(true);
    };

    const handleCloseDialog = () => {
        setOpen(false);
        setIsEdit(false);
        setSelectedRegionId("");
    };

    const getStatusChipStyles = (status) => {
        const s = (status || '').toString().toLowerCase();
        if (s === 'active') return { backgroundColor: '#D1FAE5', color: '#047857' };
        if (s === 'inactive') return { backgroundColor: '#FECACA', color: '#B91C1C' };
        if (s === 'pending') return { backgroundColor: '#FDE68A', color: '#92400E' };
        return { backgroundColor: '#E5E7EB', color: '#111827' };
    };

    return (
        <Box sx={{ minHeight: '100vh', width: '100%', mt: 2 }}>
            {/* Header */}
            <Typography fontSize={18} fontWeight={600} color="#111827" mb={2}>
                Region Management
            </Typography>
            {/* Search + Button */}
            <Box
                sx={{
                    display: "flex",
                    gap: 2,
                    flexWrap: "wrap",
                    justifyContent: "space-between",
                    mb: 3,
                    // width: { xs: 330, sm: 'auto' },
                    p: 2,
                    backgroundColor: '#fff',
                    borderRadius: 3,
                    boxShadow: "0px 4px 12px rgba(0,0,0,0.08)",
                }}
            >
                <TextField
                    placeholder="Search ..."
                    size="small"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{
                        width: { xs: 300, sm: 300 },
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                        },
                        '& .MuiInputBase-input': {
                            borderRadius: 3,
                        },
                    }}
                />

                <Button
                    variant="contained"
                    fullWidth
                    startIcon={<AddIcon />}
                    sx={{
                        bgcolor: "#4F46E5",
                        textTransform: "none",
                        borderRadius: 3,
                        width:{xs:300, sm:300},
                        px: 3,
                    }}
                    onClick={handleAdd}
                >
                    Add New Region
                </Button>
            </Box>

            {/* Desktop (sm+) grid header and rows (match Zone style) */}
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                {/* Header */}
                <Box
                    display={{ xs: 'none', sm: 'grid' }}
                    gridTemplateColumns={{ sm: '1fr 2fr 1fr 1fr 1fr' }}
                    alignItems="center"
                    px={{ xs: 1, sm: 2 }}
                    py={{ xs: 1, sm: 2 }}
                    mb={{ xs: 1, sm: 1 }}
                >
                    {['REGION ID', 'REGION NAME', 'ZONE', 'STATUS', 'ACTIONS'].map((label, i) => (
                        <Typography
                            key={label}
                            fontSize={{ sm: 10, md: 12 }}
                            color="#000000"
                            fontWeight={700}
                            sx={{ textTransform: 'uppercase', textAlign: i === 3 ? 'center' : i === 4 ? 'right' : 'left' }}
                        >
                            {label}
                        </Typography>
                    ))}
                </Box>

                {/* Rows */}
                {regionsLoading && (
                    <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #E5E7EB', px: 2, py: 1.5, mb: 2 }}>
                        <Typography variant="body2">Loading regions...</Typography>
                    </Paper>
                )}
                {!regionsLoading && paginatedRegions.map(({ regionId, regionName, zoneId, zoneName, status }) => {
                    const chipSx = {
                        width: 90,
                        height: 24,
                        ...getStatusChipStyles(status),
                        '& .MuiChip-label': { fontSize: 12, fontWeight: 600 },
                    };
                    return (
                        <Paper
                            key={regionId || regionName}
                            elevation={0}
                            sx={{
                                borderRadius: 3,
                                display: { xs: 'none', sm: 'grid' },
                                gridTemplateColumns: { sm: '1fr 2fr 1fr 1fr 1fr' },
                                alignItems: 'center',
                                border: '1px solid #E5E7EB',
                                px: 2,
                                py: 1.5,
                                gap: 0,
                                columnGap: 0,
                                mb: 1.25,
                                '&:hover': {
                                    border: '2px solid #2563EB',
                                    boxShadow: '0 4px 14px rgba(37,99,235,0.18)',
                                },
                            }}
                        >
                            <Typography variant="body2" sx={{ textAlign: 'left' }}>{regionId}</Typography>
                            <Typography variant="body2" sx={{ textAlign: 'left' }}>{regionName}</Typography>
                            <Typography variant="body2" sx={{ textAlign: 'left' }}>{zoneName || zoneId}</Typography>
                            <Box sx={{ justifySelf: 'center' }}>
                                <Chip label={String(status).charAt(0).toUpperCase() + String(status).slice(1)} size="small" sx={chipSx} />
                            </Box>
                            <Box display="flex" alignItems="center" gap={1} justifyContent="flex-end" sx={{ justifySelf: 'end' }}>
                                <IconButton size="small" aria-label="edit region" onClick={() => handleEdit(regionId)}>
                                    <EditOutlinedIcon fontSize="small" sx={{ color: '#2563EB', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
                                </IconButton>
                            </Box>
                        </Paper>
                    );
                })}
            </Box>

            {/* Cards for xs screens */}
            <Grid container spacing={2} sx={{ display: { xs: 'flex', sm: 'none' } }} justifyContent="center">
                {!regionsLoading && paginatedRegions.map(({ regionId, regionName, zoneId, zoneName, status }) => {
                    return (
                    <Grid item xs={12} key={regionId || regionName} sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                borderRadius: 3,
                                width: 300,
                                backgroundColor: '#fff',
                                border: '1px solid #E5E7EB',
                                boxShadow: '0 4px 14px rgba(37,99,235,0.18)',
                                '&:hover': {
                                    border: '2px solid #2563EB',
                                    boxShadow: '0 4px 14px rgba(37,99,235,0.18)',
                                },
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
                                <Box />
                                    <Chip
                                        label={status}
                                        size="small"
                                        sx={{ ...getStatusChipStyles(status), fontWeight: 600 }}
                                    />
                            </Box>
                            <Box sx={{ mb: 1 }}>
                                <Typography variant="body2" fontWeight={600} color="#0F172A">Region: {regionName}</Typography>
                                <Typography variant="body2" fontWeight={600} color="#64748B">Region ID: {regionId}</Typography>
                                <Typography variant="body2" fontWeight={600} color="#64748B">Zone: {zoneName || zoneId}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                <IconButton size="small" onClick={() => handleEdit(regionId)}><EditOutlinedIcon fontSize="small" sx={{ color: '#2563EB' }} /></IconButton>
                            </Box>
                        </Paper>
                    </Grid>
                    );
                })}
            </Grid>
            <AddRegion
                open={open}
                onClose={handleCloseDialog}
                isEdit={isEdit}
                regionId={selectedRegionId}
                onSuccess={() => dispatch(getAllRegionsAction())}
            />

            {/* Footer Pagination */}
            {!regionsLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                        Showing {showingStart}-{showingEnd} of {filteredRegions.length}
                    </Typography>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(e, value) => setPage(value)}
                        size="small"
                        renderItem={(item) => (
                            <PaginationItem
                                {...item}
                                sx={{
                                    borderRadius: '50%',
                                    minWidth: 32,
                                    height: 32,
                                    '&.Mui-selected': {
                                        backgroundColor: '#2563EB',
                                        color: '#FFFFFF',
                                    },
                                    '&.Mui-selected:hover': {
                                        backgroundColor: '#2563EB',
                                        color: '#FFFFFF',
                                    },
                                }}
                            />
                        )}
                    />
                </Box>
            )}
        </Box>
    );
};

export default Region;
